import {useLocale, useMessages} from "i18n";
import {formatMoney} from "pricing/PricingStorage";
import {useReferralIntroPrices} from "referral/useReferralIntroPrices";
import {useReferralCard} from "referral/useReferralCard";
import type {ReferralCardModel} from "referral/referralCard";
import {PeelCodeTag, PeelTagSlot} from "peel/PeelTag";
import {useInviteShare} from "referral/useInviteShare";
import {GiftIcon} from "download/recipient/icons";
import {CardRoot, CardText} from "account/components/AccountCards";
import {CardBody, Note, PlainCard, Stat, StatLabel, Stats, StatValue, Title} from "./ReferFriendTag.styles";

/** Plain billing card with a pinned code tag. The model still owns rewards, totals and eligibility. */
export default function ReferFriendTag() {
	const m = useMessages();
	const {model, loading, failed} = useReferralCard();
	if (loading || failed) {
		return <CardRoot>
			<Title><GiftIcon size={20}/>{m.account.referral.title}</Title>
			<CardText>{loading ? m.account.referral.loading : m.account.referral.failed}</CardText>
		</CardRoot>;
	}
	if (!model) return null;
	return <AccountReferral model={model}/>;
}

function AccountReferral({model}: {model: ReferralCardModel}) {
	const m = useMessages();
	const {locale} = useLocale();
	const gift = useReferralIntroPrices();
	return <PlainCard data-testid="referral-account-card">
		<PeelTagSlot pinned candidates={model.shareLink === null ? [] : [{
			id: "account-gift", family: "gift", priority: 0,
			node: <AccountInviteTag code={model.code} url={model.shareLink.url} label={m.account.referral.codeLabel}/>,
		}]}>
			<CardBody $tagged={model.shareLink !== null}>
				<Title><GiftIcon size={20}/>{m.account.referral.title}</Title>
				<CardText>{m.account.referral.intro[model.rewardStory]}</CardText>
				{gift !== null && <CardText data-testid="refer-friend-gift">{m.account.referral.friendGift(
					formatMoney(gift.currency, gift.plus, locale), formatMoney(gift.currency, gift.premium, locale), gift.introDays,
				)}</CardText>}
				<Stats>
					<Stat><StatValue>{model.pendingCount}</StatValue><StatLabel>{m.account.referral.pendingLabel}</StatLabel></Stat>
					<Stat><StatValue>{model.vestedCount}</StatValue><StatLabel>{m.account.referral.vestedLabel}</StatLabel></Stat>
					{model.hasEarned && <Stat><StatValue>{model.earnedCredit}</StatValue><StatLabel>{m.account.referral.earnedLabel}</StatLabel></Stat>}
				</Stats>
				<Note>{model.capReached ? m.account.referral.capReached : m.account.referral.howItWorks}</Note>
			</CardBody>
		</PeelTagSlot>
	</PlainCard>;
}

/**
 * The account card's invite tag, split off so `useInviteShare` is only mounted where a link exists.
 *
 * The slot above builds no candidate at all without one, so the hook would otherwise be asked to copy
 * an empty string on every card that is still loading its summary.
 */
function AccountInviteTag({code, url, label}: {code: string; url: string; label: string}) {
	const share = useInviteShare(url);
	return <PeelCodeTag code={code} url={url} label={label} share={share}/>;
}
